/**
 * Synthesized board sounds via the Web Audio API — no external audio files, so
 * nothing extra to download and nothing to 404. A wooden knock for moves, a
 * sharper double-knock for captures, a two-tone alert for check, and a short
 * cadence at game end.
 */

let ctx: AudioContext | null = null;
let enabled = ((): boolean => {
  // Guarded for the same reason as the piece style: this runs at load, and
  // storage can throw (a framed widget with storage blocked).
  try { return localStorage.getItem("xq-sound") !== "off"; } catch { return true; }
})();

function ac(): AudioContext | null {
  if (typeof AudioContext === "undefined") return null;
  ctx ??= new AudioContext();
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** Browsers require a user gesture before audio can start. */
export function unlockAudio(): void {
  ac();
}

export function soundEnabled(): boolean {
  return enabled;
}

export function toggleSound(): boolean {
  enabled = !enabled;
  localStorage.setItem("xq-sound", enabled ? "on" : "off");
  if (enabled) unlockAudio();
  return enabled;
}

/** Short filtered noise burst — the "wood" part of a piece landing. */
function knock(c: AudioContext, t: number, freq: number, gain: number, dur: number) {
  const len = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) {
    // Exponentially decaying noise.
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3);
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = freq;
  bp.Q.value = 1.2;
  const g = c.createGain();
  g.gain.value = gain;
  src.connect(bp).connect(g).connect(c.destination);
  src.start(t);
  src.stop(t + dur);
}

/** Pitched body — gives the knock weight. */
function thump(c: AudioContext, t: number, freq: number, gain: number, dur: number, type: OscillatorType = "triangle") {
  const o = c.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  const g = c.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(gain, t + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g).connect(c.destination);
  o.start(t);
  o.stop(t + dur + 0.02);
}

export function playMove(): void {
  if (!enabled) return;
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  thump(c, t, 190, 0.28, 0.09);
  knock(c, t, 1500, 0.16, 0.05);
}

export function playCapture(): void {
  if (!enabled) return;
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  thump(c, t, 150, 0.34, 0.12);
  knock(c, t, 2200, 0.26, 0.07);
  // Second, softer contact.
  thump(c, t + 0.055, 110, 0.2, 0.1);
  knock(c, t + 0.055, 1100, 0.14, 0.05);
}

export function playCheck(): void {
  if (!enabled) return;
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  thump(c, t, 700, 0.16, 0.14, "sine");
  thump(c, t + 0.13, 950, 0.16, 0.18, "sine");
}

export function playEnd(won: boolean | null): void {
  if (!enabled) return;
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  const notes = won === null ? [520, 520] : won ? [523, 659, 784] : [523, 415, 330];
  notes.forEach((f, i) => thump(c, t + i * 0.16, f, 0.18, 0.3, "sine"));
}

export function playSelect(): void {
  if (!enabled) return;
  const c = ac();
  if (!c) return;
  knock(c, c.currentTime, 2600, 0.06, 0.03);
}

/**
 * A chat message arrived. Two soft rising notes — clearly not a move and
 * clearly not the end of a game, because it will fire while you are thinking.
 */
export function playNotify(): void {
  if (!enabled) return;
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  thump(c, t, 880, 0.09, 0.14, "sine");
  thump(c, t + 0.1, 1175, 0.1, 0.12, "sine");
}

export function playTick(): void {
  if (!enabled) return;
  const c = ac();
  if (!c) return;
  knock(c, c.currentTime, 3200, 0.05, 0.02);
}

// ---------------------------------------------------------------- spoken moves
/**
 * Reads a move out loud with the browser's own speech synthesis — no audio to
 * download, and it speaks Chinese notation in Chinese when a 中文 voice exists.
 * Announcements never queue up: a new move cancels whatever is still being read,
 * which matters in bullet where moves arrive faster than a sentence takes.
 */
export function speak(text: string, lang: "zh" | "en" | "vi"): void {
  if (!text) return;
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang === "zh" ? "zh-CN" : lang === "vi" ? "vi-VN" : "en-US";
    u.rate = lang === "zh" ? 1 : 1.05;
    const voice = synth.getVoices().find((v) => v.lang.toLowerCase().startsWith(lang));
    if (voice) u.voice = voice;
    synth.cancel();
    synth.speak(u);
  } catch {
    /* speech is a nicety; never let it break the board */
  }
}

/** Silence anything still being read (leaving a game, switching the option off). */
export function stopSpeaking(): void {
  try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
}
